import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AssetManagementService } from '../services/asset-management.service';
import * as AssetManagementActions from './asset-management.actions';
import { Store } from '@ngrx/store';
import { selectAssetManagementState } from './asset-management.selectors';

@Injectable()
export class AssetManagementEffects {
  private readonly translate = inject(TranslateService);

  loadNetworks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetManagementActions.loadNetworks),
      switchMap(() =>
        this.assetManagementService.getNetworks().pipe(
          map(response =>
            AssetManagementActions.loadNetworksSuccess({
              response,
            })
          ),
          catchError(error =>
            of(
              AssetManagementActions.loadNetworksFailure({
                error: error?.message || 'Failed to load networks',
              })
            )
          )
        )
      )
    )
  );

  loadAssetNetworks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetManagementActions.loadAssetNetworks),
      switchMap(({ query }) =>
        this.assetManagementService.getAssetNetworks(query).pipe(
          map(response =>
            AssetManagementActions.loadAssetNetworksSuccess({
              assetNetworks: response.data || [],
            })
          ),
          catchError(error =>
            of(
              AssetManagementActions.loadAssetNetworksFailure({
                error: error?.message || 'Failed to load asset networks',
              })
            )
          )
        )
      )
    )
  );

  loadAddresses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetManagementActions.loadWhitelistAddresses),
      withLatestFrom(this.store.select(selectAssetManagementState)),
      switchMap(([, state]) =>
        this.assetManagementService
          .getWhitelistAddresses(
            state.whitelistAddressesPagination.page,
            state.whitelistAddressesPagination.limit,
            state.whitelistAddressesSort,
            state.whitelistAddressesSearchTerm
          )
          .pipe(
            map(response =>
              AssetManagementActions.loadWhitelistAddressesSuccess({
                data: response.data,
                pagination: response.pagination,
              })
            ),
            catchError(error =>
              of(
                AssetManagementActions.loadWhitelistAddressesFailure({
                  error:
                    error?.message ||
                    this.translate.instant(
                      'assets.whitelist.errors.loadFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  loadWhitelistAddressesMinimal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetManagementActions.loadWhitelistAddressesMinimal),
      switchMap(({ assetNetworkId }) =>
        this.assetManagementService
          .getWhitelistAddressesMinimal(assetNetworkId)
          .pipe(
            map(response =>
              AssetManagementActions.loadWhitelistAddressesMinimalSuccess({
                data: response.data,
              })
            ),
            catchError(error =>
              of(
                AssetManagementActions.loadWhitelistAddressesMinimalFailure({
                  error:
                    error?.message ||
                    this.translate.instant(
                      'assets.whitelist.errors.loadFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  loadBalanceHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetManagementActions.loadBalanceHistory),
      switchMap(({ vaultId, walletId, period }) =>
        this.assetManagementService
          .getBalanceHistory(vaultId, walletId, period)
          .pipe(
            map(response =>
              AssetManagementActions.loadBalanceHistorySuccess({
                data: response.data,
              })
            ),
            catchError(error =>
              of(
                AssetManagementActions.loadBalanceHistoryFailure({
                  error: error?.message || 'Failed to load balance history',
                })
              )
            )
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private assetManagementService: AssetManagementService,
    private router: Router,
    private store: Store
  ) {}
}
